import * as yup from 'yup';

import {
  MAX_DIMENSIONS,
  NOTE_MAX_LENGTH,
  NOTE_REQUIRED_MESSAGE,
  NOTE_TOO_LONG_MESSAGE,
  TITLE_MAX_LENGTH,
  TITLE_REQUIRED_MESSAGE,
  TITLE_TOO_LONG_MESSAGE,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import { sortDimensions } from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.helpers';
import type {
  CaptureFormValues,
  DimensionDefinition,
  DimensionFormValue,
  FormDrawerMode,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';

import type {
  CriticalPopulationCreateRequest,
  CriticalPopulationResponse,
  CriticalPopulationTitlePreviewRequest,
  CriticalPopulationUpdateRequest,
} from 'api/generated/schemas';

const DUPLICATE_ATTRIBUTE_MESSAGE = 'Each dimension can be used once';
const ATTRIBUTE_REQUIRED_MESSAGE = 'Select a dimension';
const ATTRIBUTE_INVALID_MESSAGE = 'Select a valid dimension';
const VALUES_REQUIRED_MESSAGE = 'Select at least one value';
const VALUES_INVALID_MESSAGE = 'Select valid values';
const SINGLE_VALUE_MESSAGE = 'Select exactly one value';

const findDefinition = (
  catalog: DimensionDefinition[],
  attribute: string | undefined
): DimensionDefinition | undefined =>
  catalog.find((definition) => definition.attribute === attribute);

export const buildFormSchema = (
  catalog: DimensionDefinition[],
  mode: FormDrawerMode
): yup.ObjectSchema<CaptureFormValues> => {
  const attributes = catalog.map(({ attribute }) => attribute);

  const dimensionSchema = yup.object({
    attribute: yup
      .string()
      .required(ATTRIBUTE_REQUIRED_MESSAGE)
      .oneOf(attributes, ATTRIBUTE_INVALID_MESSAGE),
    values: yup
      .array()
      .of(yup.string().required())
      .required(VALUES_REQUIRED_MESSAGE)
      .min(1, VALUES_REQUIRED_MESSAGE)
      .test('values-in-catalog', VALUES_INVALID_MESSAGE, function (values) {
        const definition = findDefinition(catalog, this.parent.attribute);

        return (
          !definition ||
          (values ?? []).every((value) => definition.values.includes(value))
        );
      })
      .test('single-select', SINGLE_VALUE_MESSAGE, function (values) {
        const definition = findDefinition(catalog, this.parent.attribute);

        return !definition?.singleSelect || (values ?? []).length === 1;
      }),
  });

  return yup.object({
    dimensions: yup
      .array()
      .of(dimensionSchema)
      .required()
      .min(1)
      .max(MAX_DIMENSIONS)
      .test('unique-attributes', function (dimensions) {
        const seen = new Set<string>();
        const duplicateIndexes: number[] = [];

        (dimensions ?? []).forEach((dimension, index) => {
          if (!dimension.attribute) return;
          if (seen.has(dimension.attribute)) duplicateIndexes.push(index);
          seen.add(dimension.attribute);
        });

        return duplicateIndexes.length
          ? new yup.ValidationError(
              duplicateIndexes.map(
                (index) =>
                  new yup.ValidationError(
                    DUPLICATE_ATTRIBUTE_MESSAGE,
                    dimensions,
                    `dimensions[${index}].attribute`
                  )
              )
            )
          : true;
      }),
    note:
      mode === 'create'
        ? yup
            .string()
            .trim()
            .required(NOTE_REQUIRED_MESSAGE)
            .max(NOTE_MAX_LENGTH, NOTE_TOO_LONG_MESSAGE)
        : yup.string(),
  });
};

export const EMPTY_DIMENSION: DimensionFormValue = {
  attribute: '',
  values: [],
};

export const rowToFormValues = (
  row: CriticalPopulationResponse
): CaptureFormValues => ({
  dimensions: sortDimensions(row.dimensions).map(
    ({ attribute, values = [] }) => ({ attribute: attribute ?? '', values })
  ),
});

const toSelections = (
  dimensions: DimensionFormValue[]
): CriticalPopulationUpdateRequest['dimensions'] =>
  dimensions.map(({ attribute, values }) => ({ attribute, values }));

export const validateTitle = (title: string): string | undefined => {
  const trimmed = title.trim();

  if (!trimmed) return TITLE_REQUIRED_MESSAGE;
  if (trimmed.length > TITLE_MAX_LENGTH) return TITLE_TOO_LONG_MESSAGE;

  return undefined;
};

export const toCreateRequest = (
  values: CaptureFormValues,
  enteringClassYear: number,
  title?: string
): CriticalPopulationCreateRequest => ({
  enteringClassYear,
  dimensions: toSelections(values.dimensions),
  note: (values.note ?? '').trim(),
  ...(title !== undefined && { title }),
});

export const toUpdateRequest = (
  values: CaptureFormValues,
  title?: string
): CriticalPopulationUpdateRequest => ({
  dimensions: toSelections(values.dimensions),
  ...(title !== undefined && { title }),
});

const isDimensionComplete = (
  dimension: DimensionFormValue,
  definition: DimensionDefinition
): boolean => {
  const selected = definition.values.filter((value) =>
    dimension.values.includes(value)
  );

  return definition.singleSelect
    ? dimension.values.length === 1 && selected.length === 1
    : selected.length > 0;
};

export const toPreviewDimensions = (
  dimensions: DimensionFormValue[],
  catalog: DimensionDefinition[]
): CriticalPopulationTitlePreviewRequest['dimensions'] | undefined => {
  if (!dimensions.length) return undefined;

  const complete = dimensions.every((dimension) => {
    const definition = findDefinition(catalog, dimension.attribute);

    return !!definition && isDimensionComplete(dimension, definition);
  });

  return complete ? toSelections(dimensions) : undefined;
};
